import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  Heading,
  Icon,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  SimpleGrid,
  Skeleton,
  Spinner,
  Stack,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { ArrowBackIcon, ChevronDownIcon, SearchIcon } from "@chakra-ui/icons";
import { ChevronLeft, ChevronRight } from "lucide-react";

import type { MarriageProfileDto } from "src/types/Profile";
import type { MatrimonyProfileDetailsData } from "src/types/matrimonyProfileDetails";
import { MarriageProfileService } from "src/rest/marriageProfileService";
import { fetchAuthedImageObjectUrl } from "src/utils/authImage";
import { UseAppToast } from "src/common/useAppToast";

import { mapDtoToPageData } from "src/pages/matrimony/pages/matrimonyProfileDetails.utils";

import {
  AboutSection,
  AstroSection,
  FamilySection,
  LifestyleSection,
  PersonalSection,
  ProfessionalSection,
} from "src/pages/matrimony/components/sections";

import ChangeReasonModal from "src/pages/matrimony/components/ChangeReasonModal";
import ChangeHistoryIcon from "src/resources/icons/ChangeHistoryIcon";
import type { AuditLogEntry, AdminMatrimonyProfileEditProps } from "src/types/adminMatrimonyProfileEdit.types";
import {
  AUDIT_PAGE_SIZE,
  fmtDate,
  filterAndSortAuditLogs,
  getUniqueActors,
} from "src/utils/adminMatrimonyProfileEdit.utils";

export type { AdminMatrimonyProfileEditProps };

// ── Component ────────────────────────────────────────────────────────

const AdminMatrimonyProfileEdit: React.FC<AdminMatrimonyProfileEditProps> = ({
  profileId,
  onBack,
}) => {
  const { showSuccessToast, showErrorToast } = UseAppToast();
  const toastRef = useRef({ showSuccessToast, showErrorToast });
  toastRef.current = { showSuccessToast, showErrorToast };

  // ── State ───────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [marriageDto, setMarriageDto] = useState<MarriageProfileDto | null>(null);

  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [additionalPhotoUrls, setAdditionalPhotoUrls] = useState<string[]>([]);

  const [activeValue, setActiveValue] = useState(true);
  const [isTogglingActive, setIsTogglingActive] = useState(false);
  const [pendingToggleValue, setPendingToggleValue] = useState<boolean | null>(null);
  const [isToggleReasonModalOpen, setIsToggleReasonModalOpen] = useState(false);

  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditSearch, setAuditSearch] = useState("");
  const [auditActorFilter, setAuditActorFilter] = useState("All Actors");
  const [auditPage, setAuditPage] = useState(0);

  // ── Lightbox ────────────────────────────────────────────────────────
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(0);

  // ── Derived data ────────────────────────────────────────────────────

  const pageData: MatrimonyProfileDetailsData | null = useMemo(() => {
    if (!marriageDto) return null;
    return mapDtoToPageData(marriageDto);
  }, [marriageDto]);

  const pageDataWithPhoto = useMemo(() => {
    if (!pageData) return null;
    if (!photoUrl) return pageData;
    return { ...pageData, summary: { ...pageData.summary, photoUrl } };
  }, [pageData, photoUrl]);

  // All photos for lightbox: primary first, then gallery.
  const allPhotos = useMemo(
    () => [photoUrl, ...additionalPhotoUrls].filter(Boolean) as string[],
    [photoUrl, additionalPhotoUrls],
  );

  const auditActors = useMemo(() => getUniqueActors(auditLogs), [auditLogs]);

  const filteredAuditLogs = useMemo(
    () => filterAndSortAuditLogs(auditLogs, auditActorFilter, auditSearch),
    [auditLogs, auditActorFilter, auditSearch],
  );

  // Reset to page 0 when filter / search changes
  useEffect(() => { setAuditPage(0); }, [auditActorFilter, auditSearch]);

  const auditTotalPages = Math.max(1, Math.ceil(filteredAuditLogs.length / AUDIT_PAGE_SIZE));

  const visibleAuditLogs = useMemo(() => {
    const start = auditPage * AUDIT_PAGE_SIZE;
    return filteredAuditLogs.slice(start, start + AUDIT_PAGE_SIZE);
  }, [filteredAuditLogs, auditPage]);

  // ── Lightbox helpers ────────────────────────────────────────────────
  const openLightbox = (idx: number) => { setLightboxIdx(idx); setLightboxOpen(true); };
  const closeLightbox = () => setLightboxOpen(false);
  const lightboxPrev = () => setLightboxIdx((i) => (i - 1 + allPhotos.length) % allPhotos.length);
  const lightboxNext = () => setLightboxIdx((i) => (i + 1) % allPhotos.length);

  // ── Fetch profile ───────────────────────────────────────────────────

  const loadProfile = useCallback(async () => {
    try {
      setLoading(true);
      const dto = (await MarriageProfileService.getById(profileId)) as MarriageProfileDto;
      setMarriageDto(dto);
      if (dto.active != null) setActiveValue(Boolean(dto.active));
    } catch (err: any) {
      toastRef.current.showErrorToast(
        err?.response?.data?.message || err?.message || "Failed to load profile.",
      );
    } finally {
      setLoading(false);
    }
  }, [profileId]);

  useEffect(() => { void loadProfile(); }, [loadProfile]);

  // ── Fetch profile photo ─────────────────────────────────────────────

  useEffect(() => {
    const fileName = marriageDto?.profilePhoto;
    if (!fileName) { setPhotoUrl(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const url = await fetchAuthedImageObjectUrl(fileName);
        if (cancelled) { URL.revokeObjectURL(url); return; }
        setPhotoUrl(url);
      } catch { if (!cancelled) setPhotoUrl(null); }
    })();
    return () => { cancelled = true; };
  }, [marriageDto?.profilePhoto]);

  useEffect(() => {
    return () => { if (photoUrl) URL.revokeObjectURL(photoUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Fetch additional gallery photos ─────────────────────────────────

  const additionalImageKey = useMemo(() => {
    const images = marriageDto?.images;
    const fileNames = Array.isArray(images) && images.length > 0
      ? images.map((img) => img.fileName).filter(Boolean) : [];
    return JSON.stringify(fileNames);
  }, [marriageDto?.images]);

  useEffect(() => {
    const fileNames: string[] = JSON.parse(additionalImageKey);
    if (fileNames.length === 0) { setAdditionalPhotoUrls([]); return; }
    let cancelled = false;
    const urls: string[] = [];
    (async () => {
      for (const name of fileNames) {
        if (!name || cancelled) continue;
        try {
          const url = await fetchAuthedImageObjectUrl(name);
          if (cancelled) { URL.revokeObjectURL(url); return; }
          urls.push(url);
        } catch { /* skip */ }
      }
      if (!cancelled) setAdditionalPhotoUrls(urls);
    })();
    return () => { cancelled = true; urls.forEach((u) => URL.revokeObjectURL(u)); };
  }, [additionalImageKey]);

  useEffect(() => {
    return () => { additionalPhotoUrls.forEach((u) => URL.revokeObjectURL(u)); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Fetch audit logs ────────────────────────────────────────────────

  const loadAuditLogs = useCallback(async () => {
    try {
      setAuditLoading(true);
      const logs = await MarriageProfileService.getAuditLogs(profileId);
      setAuditLogs(Array.isArray(logs) ? logs : []);
    } catch { setAuditLogs([]); }
    finally { setAuditLoading(false); }
  }, [profileId]);

  useEffect(() => { void loadAuditLogs(); }, [loadAuditLogs]);

  // ── Active toggle ───────────────────────────────────────────────────

  const handleToggleActive = (next: boolean) => {
    if (!marriageDto?.id) return;
    setPendingToggleValue(next);
    setIsToggleReasonModalOpen(true);
  };

  const handleToggleReasonConfirm = async (reason: string) => {
    if (!marriageDto?.id || pendingToggleValue == null) return;
    const next = pendingToggleValue;
    const previous = activeValue;
    setActiveValue(next);
    setIsTogglingActive(true);
    setIsToggleReasonModalOpen(false);
    try {
      const existingLinks = marriageDto?.socialMediaLinksList;
      const patchData: Record<string, any> = { active: next };
      if (Array.isArray(existingLinks) && existingLinks.length > 0) {
        patchData.socialMediaLinksList = existingLinks;
      }
      const res = await MarriageProfileService.updatePatch(marriageDto.id, {
        data: patchData,
        changeReason: reason,
      });
      const updated = (res as any)?.data;
      if (updated && typeof updated === "object") {
        setMarriageDto(updated as MarriageProfileDto);
      } else {
        setMarriageDto((p) => (p ? ({ ...p, active: next } as any) : p));
      }
      showSuccessToast(next ? "Profile marked Active." : "Profile marked Inactive.");
      void loadAuditLogs();
    } catch (e: any) {
      setActiveValue(previous);
      showErrorToast(e?.response?.data?.message || e?.message || "Please try again.");
    } finally {
      setIsTogglingActive(false);
      setPendingToggleValue(null);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box py={10} display="flex" justifyContent="center">
        <Spinner size="lg" color="#4A2C2A" />
      </Box>
    );
  }

  if (!marriageDto || !pageDataWithPhoto) {
    return (
      <Box>
        <Button leftIcon={<ArrowBackIcon />} variant="link" color="#6B7280" mb={4} onClick={onBack}>
          Back to Profiles
        </Button>
        <Text color="#6B7280" textAlign="center" py={10}>Profile not found.</Text>
      </Box>
    );
  }

  const profileName = marriageDto.fullName || "Unknown";
  const profileIdStr = `(M${marriageDto.id})`;

  return (
    <Box>
      {/* ── Page Header ──────────────────────────────────────────────── */}
      <Box mb={6}>
        <Button
          leftIcon={<ArrowBackIcon />}
          variant="link"
          color="#6B7280"
          fontWeight="500"
          fontSize="14px"
          mb={2}
          onClick={onBack}
          _hover={{ color: "#4A2C2A", textDecoration: "underline" }}
        >
          Back to Profiles
        </Button>
        <Heading fontSize="24px" color="#1F2937" fontWeight="700">
          Profile: {profileName}{" "}
          <Text as="span" color="#9CA3AF" fontWeight="500">{profileIdStr}</Text>
        </Heading>
      </Box>

      {/* ── Change History ───────────────────────────────────────────── */}
      <Box bg="white" borderRadius="16px" border="1px solid #C6D0DC" mb={6} overflow="hidden">
        <Flex px={6} py={4} align="center" justify="space-between" flexWrap="wrap" gap={3}>
          <HStack spacing={2}>
            <ChangeHistoryIcon />
            <Heading fontSize="20px" color="#1F2937" fontWeight="700">Change History</Heading>
          </HStack>
          <HStack spacing={3}>
            <Menu>
              <MenuButton
                as={Button}
                variant="outline"
                size="sm"
                borderRadius="8px"
                borderColor="#E5E7EB"
                color="#4A5568"
                fontWeight="500"
                rightIcon={<ChevronDownIcon />}
                leftIcon={
                  <Icon viewBox="0 0 16 16" boxSize="14px" color="#6B7280">
                    <path d="M1 2h14M4 7h8M6 12h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" />
                  </Icon>
                }
                _hover={{ bg: "#F9FAFB" }}
              >
                {auditActorFilter}
              </MenuButton>
              <MenuList minW="160px" borderRadius="10px" boxShadow="lg">
                <MenuItem fontSize="sm" onClick={() => setAuditActorFilter("All Actors")} fontWeight={auditActorFilter === "All Actors" ? "600" : "400"}>
                  All Actors
                </MenuItem>
                {auditActors.map((actor) => (
                  <MenuItem key={actor} fontSize="sm" onClick={() => setAuditActorFilter(actor)} fontWeight={auditActorFilter === actor ? "600" : "400"}>
                    {actor}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
            <InputGroup size="sm" maxW="200px">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" boxSize="14px" />
              </InputLeftElement>
              <Input
                placeholder="Search logs..."
                value={auditSearch}
                onChange={(e) => setAuditSearch(e.target.value)}
                borderRadius="8px"
                borderColor="#E5E7EB"
                bg="white"
                fontSize="sm"
                _placeholder={{ color: "#9CA3AF" }}
              />
            </InputGroup>
          </HStack>
        </Flex>

        <TableContainer>
          <Table variant="simple" size="md">
            <Thead>
              <Tr borderBottom="1px solid #E5E7EB">
                {["Field Changed", "Old Value", "New Value", "Changed By", "Timestamp", "Reason"].map((h) => (
                  <Th key={h} color="#6B7280" fontSize="xs" textTransform="uppercase" fontWeight="600" letterSpacing="0.05em">{h}</Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {auditLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <Tr key={`skel-${i}`}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <Td key={j}><Skeleton h="16px" w="80px" /></Td>
                    ))}
                  </Tr>
                ))
              ) : visibleAuditLogs.length === 0 ? (
                <Tr>
                  <Td colSpan={6} textAlign="center" py={8}>
                    <Text color="#9CA3AF" fontSize="sm">No change history found.</Text>
                  </Td>
                </Tr>
              ) : (
                visibleAuditLogs.map((log, idx) => (
                  <Tr key={idx} _hover={{ bg: "#FAFAF9" }} borderBottom="1px solid #F3F4F6">
                    <Td fontSize="sm" fontWeight="600" color="#1F2937">{log.fieldName || "—"}</Td>
                    <Td fontSize="sm" color="#9CA3AF">{log.oldValue || "—"}</Td>
                    <Td fontSize="sm" fontWeight="600" color="#1F2937">{log.newValue || "—"}</Td>
                    <Td>
                      <HStack spacing={2}>
                        <Avatar size="xs" name={log.changedBy || "?"} />
                        <Text fontSize="sm" fontWeight="500" color="#1F2937">{log.changedBy || "—"}</Text>
                      </HStack>
                    </Td>
                    <Td fontSize="sm" color="#9CA3AF" whiteSpace="nowrap">{fmtDate(log.changedAt)}</Td>
                    <Td fontSize="sm" fontWeight="500" color="#1F2937" maxW="200px" isTruncated>{log.reason || "—"}</Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </TableContainer>

        {!auditLoading && filteredAuditLogs.length > 0 && (
          <Flex
            align="center"
            justify="space-between"
            px={6}
            py={4}
            borderTop="1px solid #F3F4F6"
            backgroundColor="#F8FAFC"
            flexWrap="wrap"
            gap={2}
          >
            <Text fontSize="sm" color="#6B7280">
              Showing {auditPage * AUDIT_PAGE_SIZE + 1}–
              {Math.min((auditPage + 1) * AUDIT_PAGE_SIZE, filteredAuditLogs.length)} of{" "}
              {filteredAuditLogs.length} {filteredAuditLogs.length === 1 ? "entry" : "entries"}
            </Text>

            <HStack spacing={1}>
              <Button
                size="sm"
                variant="ghost"
                color="#6B7280"
                isDisabled={auditPage === 0}
                onClick={() => setAuditPage((p) => p - 1)}
              >
                ‹
              </Button>
              {Array.from({ length: auditTotalPages }, (_, i) => (
                <Button
                  key={i}
                  size="sm"
                  variant={i === auditPage ? "solid" : "ghost"}
                  bg={i === auditPage ? "#4A2C2A" : undefined}
                  color={i === auditPage ? "white" : "#6B7280"}
                  _hover={{ bg: i === auditPage ? "#4A2C2A" : "#F3F4F6" }}
                  borderRadius="8px"
                  onClick={() => setAuditPage(i)}
                >
                  {i + 1}
                </Button>
              ))}
              <Button
                size="sm"
                variant="ghost"
                color="#6B7280"
                isDisabled={auditPage >= auditTotalPages - 1}
                onClick={() => setAuditPage((p) => p + 1)}
              >
                ›
              </Button>
            </HStack>
          </Flex>
        )}
      </Box>

      {/* ── Main content: Left sidebar + Right sections ──────────────── */}
      <Grid templateColumns={{ base: "1fr", lg: "300px 1fr" }} gap={6} alignItems="start">

        {/* ── Left sidebar ─────────────────────────────────────────────── */}
        <GridItem>
          <Stack spacing={5}>
            {/* Profile Photo (click to enlarge) */}
            <Box bg="white" borderRadius="12px" border="1px solid #E5E7EB" overflow="hidden">
              {photoUrl ? (
                <Image
                  src={photoUrl}
                  alt={profileName}
                  w="100%"
                  h="280px"
                  objectFit="cover"
                  cursor="pointer"
                  title="Click to enlarge"
                  onClick={() => openLightbox(0)}
                  _hover={{ opacity: 0.92 }}
                  transition="opacity 0.15s ease"
                />
              ) : (
                <Flex w="100%" h="280px" align="center" justify="center" bg="#F3F4F6">
                  <Avatar size="2xl" name={profileName} />
                </Flex>
              )}
              {additionalPhotoUrls.length > 0 && (
                <SimpleGrid columns={4} spacing={2} p={3}>
                  {additionalPhotoUrls.map((url, i) => (
                    <Image
                      key={i}
                      src={url}
                      alt={`Gallery ${i + 1}`}
                      w="100%"
                      h="60px"
                      objectFit="cover"
                      borderRadius="6px"
                      cursor="pointer"
                      title="Click to enlarge"
                      onClick={() => openLightbox(i + 1)}
                      _hover={{ opacity: 0.85, outline: "2px solid #4A2C2A" }}
                      transition="all 0.15s ease"
                    />
                  ))}
                </SimpleGrid>
              )}
            </Box>

            {/* Profile Status */}
            <Box bg="white" borderRadius="12px" border="1px solid #E5E7EB" px={5} py={4}>
              <Text fontSize="20px" fontWeight="600" color="#4A2C2A" mb={3}>Profile Status</Text>
              <Flex align="center" justify="space-between">
                <Badge
                  px={2} py={0.5}
                  borderRadius="full"
                  fontSize="xs"
                  colorScheme={activeValue ? "green" : "orange"}
                  variant="subtle"
                >
                  {activeValue ? "Active" : "Inactive"}
                </Badge>
                <Switch
                  size="md"
                  colorScheme="green"
                  isChecked={activeValue}
                  isDisabled={isTogglingActive}
                  onChange={(e) => handleToggleActive(e.target.checked)}
                />
              </Flex>
            </Box>

            {/* Registration Info */}
            <Box bg="white" borderRadius="12px" border="1px solid #E5E7EB" px={5} py={4}>
              <Text fontSize="20px" fontWeight="600" color="#4A2C2A" mb={3}>Registration Info</Text>
              <VStack align="stretch" spacing={2}>
                <Flex justify="space-between" fontSize="sm">
                  <Text color="#6B7280">Created By</Text>
                  <Text color="#1F2937" fontWeight="500">{marriageDto.createdBy || "—"}</Text>
                </Flex>
                <Flex justify="space-between" fontSize="sm">
                  <Text color="#6B7280">Created At</Text>
                  <Text color="#1F2937" fontWeight="500">{fmtDate(marriageDto.createdAt)}</Text>
                </Flex>
                <Flex justify="space-between" fontSize="sm">
                  <Text color="#6B7280">Last Updated</Text>
                  <Text color="#1F2937" fontWeight="500">{fmtDate(marriageDto.updatedAt)}</Text>
                </Flex>
              </VStack>
            </Box>

            {/* Astro — sidebar (desktop) */}
            <Box display={{ base: "none", lg: "block" }}>
              <AstroSection
                astro={pageDataWithPhoto.astro}
                dto={marriageDto}
                isOwnProfile={false}
                onEdit={() => {}}
              />
            </Box>
          </Stack>
        </GridItem>

        {/* ── Right: read-only profile sections ────────────────────────── */}
        <GridItem>
          <Stack spacing={5}>
            <AboutSection about={String(marriageDto.about || "")} isOwnProfile={false} onEdit={() => {}} />
            <PersonalSection personal={pageDataWithPhoto.personal} dto={marriageDto} isOwnProfile={false} onEdit={() => {}} />
            <ProfessionalSection professional={pageDataWithPhoto.professional} dto={marriageDto} isOwnProfile={false} onEdit={() => {}} />
            <FamilySection family={pageDataWithPhoto.family} dto={marriageDto} isOwnProfile={false} onEdit={() => {}} />
            <LifestyleSection lifestyle={pageDataWithPhoto.lifestyle} isOwnProfile={false} onEdit={() => {}} />
            <Box display={{ base: "block", lg: "none" }}>
              <AstroSection astro={pageDataWithPhoto.astro} dto={marriageDto} isOwnProfile={false} onEdit={() => {}} />
            </Box>
          </Stack>
        </GridItem>
      </Grid>

      {/* ── Active Toggle Reason Modal ─────────────────────────────── */}
      <ChangeReasonModal
        isOpen={isToggleReasonModalOpen}
        onClose={() => { setIsToggleReasonModalOpen(false); setPendingToggleValue(null); }}
        onConfirm={handleToggleReasonConfirm}
        isLoading={isTogglingActive}
        title={pendingToggleValue ? "Reason for Activating Profile" : "Reason for Deactivating Profile"}
      />

      {/* ── Photo Lightbox ─────────────────────────────────────────── */}
      <Modal isOpen={lightboxOpen} onClose={closeLightbox} size="full" isCentered>
        <ModalOverlay bg="rgba(0,0,0,0.85)" />
        <ModalContent bg="transparent" boxShadow="none" maxW="100vw" maxH="100vh" m={0}>
          <ModalCloseButton
            color="white"
            size="lg"
            zIndex={2}
            top={4}
            right={4}
            _hover={{ bg: "rgba(255,255,255,0.15)" }}
          />
          <ModalBody
            display="flex"
            alignItems="center"
            justifyContent="center"
            p={0}
            position="relative"
          >
            {allPhotos.length > 1 && (
              <IconButton
                aria-label="Previous photo"
                icon={<ChevronLeft size={28} />}
                position="absolute"
                left={{ base: 2, md: 6 }}
                top="50%"
                transform="translateY(-50%)"
                borderRadius="full"
                bg="rgba(255,255,255,0.15)"
                color="white"
                _hover={{ bg: "rgba(255,255,255,0.3)" }}
                onClick={lightboxPrev}
                zIndex={2}
              />
            )}

            <Image
              src={allPhotos[lightboxIdx]}
              alt={`Photo ${lightboxIdx + 1}`}
              maxH="90vh"
              maxW="90vw"
              objectFit="contain"
              borderRadius="12px"
            />

            {allPhotos.length > 1 && (
              <IconButton
                aria-label="Next photo"
                icon={<ChevronRight size={28} />}
                position="absolute"
                right={{ base: 2, md: 6 }}
                top="50%"
                transform="translateY(-50%)"
                borderRadius="full"
                bg="rgba(255,255,255,0.15)"
                color="white"
                _hover={{ bg: "rgba(255,255,255,0.3)" }}
                onClick={lightboxNext}
                zIndex={2}
              />
            )}

            {allPhotos.length > 1 && (
              <Text
                position="absolute"
                bottom={4}
                left="50%"
                transform="translateX(-50%)"
                color="white"
                fontSize="sm"
                fontWeight="600"
                bg="rgba(0,0,0,0.5)"
                px={3}
                py={1}
                borderRadius="full"
              >
                {lightboxIdx + 1} / {allPhotos.length}
              </Text>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AdminMatrimonyProfileEdit;
